import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import { useBreakpointValue } from '@hub/design-system-base'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { useGetHrefContext } from '@scentregroup/shared/context'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import { SearchAction, WebSite, WithContext, Organization } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import { GetServerSideProps, GetServerSidePropsContext } from '../types/next'
import { makeApolloClient } from '../lib/apolloClient'
import { getNationalHomepage } from '../lib/page-templates/fetching'
import { Template } from '../components/page-templates/template'

function useLDWebSite(url: string): WithContext<WebSite> {
  return React.useMemo<WithContext<WebSite>>(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Westfield',
      url,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${url}search?q={search_term_string}&utm_source=google&utm_medium=organic&utm_campaign=sitelinks_searchbox`,
        'query-input': 'required name=search_term_string',
      } as SearchAction & { 'query-input': string }, // schema-dts doesn't support extension properties at the moment
    }),
    [url]
  )
}

const organisationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Westfield',
  logo: 'https://d2csxpduxe849s.cloudfront.net/media/8AB6B2CD-D538-46A4-930B472438784815/F1B4B648-8B06-4F41-A660A59C49755A48/thul-D3BC2399-A471-42AB-821A6915C827C9FC.png',
  alternateName: 'Scentre Group',
  legalName: 'SCENTRE LIMITED',
  foundingDate: '19590701',
  founders: [
    {
      '@type': 'Person',
      name: 'Frank Lowy',
    },
    {
      '@type': 'Person',
      name: 'John Saunders',
    },
  ],
} as const

function useLDOrganisation(
  url: string,
  country: string
): WithContext<Organization> {
  return React.useMemo<WithContext<Organization>>(
    () => ({
      url,
      ...organisationSchema,
      ...(country === 'New Zealand'
        ? {
            address: {
              '@type': 'PostalAddress',
              streetAddress: '277 Broadway',
              addressLocality: 'Newmarket',
              addressRegion: 'Auckland',
              postalCode: '1023',
              addressCountry: 'NZ',
            },
            sameAs: [
              'https://www.facebook.com/WestfieldAUNZ',
              'https://twitter.com/westfieldnz',
              'https://www.instagram.com/westfieldnz/',
              'https://www.youtube.com/@westfieldnz',
              'https://www.linkedin.com/company/scentre-group/',
            ],
            potentialAction: {
              '@type': 'SearchAction',
              target:
                'https://www.westfield.co.nz/search?q={search_term}&utm_source=google&utm_medium=organic&utm_campaign=sitelinks_searchbox',
              'query-input': 'required name=search_term',
            },
          }
        : {
            address: {
              '@type': 'PostalAddress',
              streetAddress: '85 Castlereagh St',
              addressLocality: 'Sydney',
              addressRegion: 'NSW',
              postalCode: '2000',
              addressCountry: 'AU',
            },
            sameAs: [
              'https://www.facebook.com/WestfieldAUNZ',
              'https://twitter.com/WestfieldAU',
              'https://www.instagram.com/westfieldau/',
              'https://www.pinterest.com.au/westfieldau/',
              'https://www.youtube.com/@WestfieldAu',
              'https://www.linkedin.com/company/scentre-group/',
            ],
            potentialAction: {
              '@type': 'SearchAction',
              target:
                'https://www.westfield.com.au/search?q={search_term}&utm_source=google&utm_medium=organic&utm_campaign=sitelinks_searchbox',
              'query-input': 'required name=search_term',
            },
          }),
    }),
    [url, country]
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  res,
}: GetServerSidePropsContext) => {
  setCacheHeaders(res)
  const client = makeApolloClient()
  const country = localeToCountry(locale)

  const [nationalHeaderData, template] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getNationalHomepage(client, country),
  ])

  if (!nationalHeaderData || !template) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      country,
      nationalHeaderData,
      template,
    },
  }
}

const IndexPage: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = props => {
  const { country, template, nationalHeaderData } = props

  const displayModes: string[] = ['FEATURE']

  const headerHasBottomMarginMobile = !displayModes.includes(
    template.modules[0]?.kind
  )

  const headerHasBottomMargin = useBreakpointValue({
    base: headerHasBottomMarginMobile,
    sm: true,
  })

  const { title, shortDescription } = template
  const canonical = useGetHrefContext()()

  const ldJsonSchemaForWebSite = useLDWebSite(canonical)

  const ldJsonSchemaForOrganisation = useLDOrganisation(canonical, country)

  const defaultMetaData = getDefaultNationalMetaData({
    country,
    type: 'homepage',
  })

  const metaData = {
    title: title || defaultMetaData.title,
    description: shortDescription || defaultMetaData.description,
    canonical,
  }

  return (
    <>
      <SGLinkedData item={ldJsonSchemaForWebSite} />
      <SGLinkedData item={ldJsonSchemaForOrganisation} />
      <Layout
        country={country}
        centre={undefined}
        header={{
          variant: 'national',
          data: nationalHeaderData,
          hasBottomMargin: headerHasBottomMargin,
        }}
        metaData={metaData}
      >
        <Template template={template} />
      </Layout>
    </>
  )
}

export default IndexPage
